/**
 * Text for the (?) help icons (components/HelpTip.vue), as approved by the
 * boss in STW-1168: keep the wording, case and mark-up as written there.
 * Mark-up: `**bold**`, `_italic_`, `\n` new line, blank line new paragraph,
 * `- ` bullet (see utils/helpText.ts). To explain several things with one
 * icon, use `sections` (each with its own title); `title` is then only read
 * out by screen readers.
 */
type HelpSection = { title: string; text: string };
type HelpText = { title: string } & (
  | { text: string }
  | { sections: readonly HelpSection[] }
);

export const HELP_TEXTS = {
  // Sharing
  share: {
    title: 'Share',
    text: '**Share link:** Anyone with the link can view the model (except “Private” models).\n**Share in platform:** Send a duplicate of your model to others on the platform.',
  },
  copyPermissions: {
    title: 'Copy permissions',
    text: "Each recipient gets their own copy in their dashboard.\nChoose whether they can **Re-share**, and whether they can **edit** or **download** it.\nYour original model isn't changed.",
  },
  shareToCohort: {
    title: 'Share to cohort group',
    text: 'Share to a cohort group will send each member an invitation for a unique duplicate of your selected models.\nSharing the same model again sends a new invitation, and accepting it makes another copy.',
  },

  // Groups and students
  userGroups: {
    title: 'User groups',
    text: 'Anyone can create a group and add friends.\nShare your model to send each of them a unique duplicate.',
  },
  cohortGroups: {
    title: 'Cohort groups',
    text: 'Cohorts groups are created by admins. Profs and admins can move students between cohorts and send each of them a unique duplicated model. A student removed from their last cohort moves to “Unassigned”.',
  },
  studentDirectory: {
    title: 'Student directory',
    text: 'A view-only list of student accounts. To move a student to another cohort, use Cohort Groups.',
  },

  // Admin
  categories: {
    title: 'Categories',
    text: 'Tags that help people find models. Owners pick them in Item Settings, and everyone can filter by them. Deleting a category removes it from every model.',
  },

  // Upload and model settings
  upload: {
    title: 'What can I upload?',
    text: 'A 3D model file (.glb, .ply, .ksplat).\nA landscape/portrait 16:9 video to create a 3D model (only the first 3 minutes are used).\nUp to 300 photos to create a 3D model\nEach scan uses a scan credit.',
  },
  visibility: {
    title: 'Visibility',
    text: '**Public:** Anyone can find and view it. Visible on the “Explore” tab\n**Unlisted:** Only people with the link can view it. Not visible on the “Explore” tab\n**Private:** Only you can see it.',
  },

  // Dashboard
  itemsAndScenes: {
    title: 'Items and Scenes',
    sections: [
      {
        title: 'Items',
        text: 'Your 3D models. Open one to view it, add annotations and camera views, or share it.',
      },
      {
        title: 'Scenes',
        text: 'Use a 3D model of a room/environment as a scene. Then drag & drop 3D models inside to customize interaction. Use our _Block Editor_ to add logic to sequence custom MCQ/SAQ to quiz students using immersive walk-through experience.',
      },
    ],
  },

  // Explore (DRAFT, not in STW-1168 yet: needs the boss's approval)
  exploreItemsAndScenes: {
    title: 'Items and Scenes',
    sections: [
      {
        title: 'Items',
        text: '3D models published by everyone on the platform. Open one to view it, or create a copy on your dashboard if the owner allows it.',
      },
      {
        title: 'Scenes',
        text: 'Published 3D spaces with models placed inside. Open one to walk through it.',
      },
    ],
  },

  // 3D viewer
  layers: {
    title: 'Layers',
    text: 'Organize **annotations** and **cameras** into different layers. (i.e. nerves, veins, muscles, etc.)',
  },
  cameraViews: {
    title: 'Camera views',
    text: 'Saved camera angles that viewers can step through.\nLinking a camera to an annotation will trigger this view when the annotation is selected.\nUse the number/numpad keys to cycle through these preset views.',
  },
  annotations: {
    title: 'Annotations',
    text: 'Attach 3D Marker anywhere. Accepts:\n- Text\n- URLs\n- Images\n\n**Set Camera View** to help users navigate to exactly where you want them to look.',
  },
} as const satisfies Record<string, HelpText>;

export type HelpTextKey = keyof typeof HELP_TEXTS;
